import { Grid } from '@mui/material';
import SnapshotBox from 'components/SnapshotBox/SnapshotBox';
import Spacing from 'components/Spacing';
import {
  dateToLabel,
  numberToCurrencyString,
  numberToDisplayString,
  numberToPercentageString,
} from 'further-ui/utils';
import Typography from '@mui/material/Typography';
import { Vehicle } from 'further-types/lp/vehicle';

type AssetPerformanceAggregations = {
  wasConverted: boolean;
  totalInvestmentAmountLcy: number;
  totalCurrentValueLcy: number;
  totalCurrentUnrealisedValueLcy: number;
  totalCurrentRealisedValueLcy: number;
  grossIRR: string | number;
  grossMOIC: string;
  firstInvestmentDate: Date | undefined;
  holdingPeriod: number | null;
};

type Props = {
  selectedVehicle: Vehicle;
  assetPerformanceAggregations: AssetPerformanceAggregations;
};

const CompanyPerformanceOverview: React.FC<Props> = ({
  selectedVehicle,
  assetPerformanceAggregations,
}) => {
  return (
    <Spacing size="md">
      <Grid container justifyContent="space-between" spacing={7}>
        <SnapshotBox
          value={numberToCurrencyString(
            assetPerformanceAggregations.totalCurrentValueLcy,
            {
              currency: selectedVehicle.baseCurrency,
            },
          )}
          subtitle="Total current value"
        />
        <SnapshotBox
          value={numberToCurrencyString(
            assetPerformanceAggregations.totalCurrentUnrealisedValueLcy,
            {
              currency: selectedVehicle.baseCurrency,
            },
          )}
          subtitle="Total unrealised value"
        />
        <SnapshotBox
          value={numberToCurrencyString(
            assetPerformanceAggregations.totalCurrentRealisedValueLcy,
            {
              currency: selectedVehicle.baseCurrency,
            },
          )}
          subtitle="Total realised value"
        />
      </Grid>
      <Grid container justifyContent="space-between" spacing={7}>
        <SnapshotBox
          value={numberToCurrencyString(
            assetPerformanceAggregations.totalInvestmentAmountLcy,
            {
              currency: selectedVehicle.baseCurrency,
            },
          )}
          subtitle="Agg. investment cost"
        />
        <SnapshotBox
          value={assetPerformanceAggregations.grossMOIC}
          subtitle="Gross MOIC"
        />
      </Grid>
      <Grid container justifyContent="space-between" spacing={7}>
        <SnapshotBox
          value={dateToLabel(assetPerformanceAggregations.firstInvestmentDate)}
          subtitle="First investment date"
        />
        <SnapshotBox
          value={numberToDisplayString(
            assetPerformanceAggregations.holdingPeriod ?? 0,
            {
              style: 'unit',
              unit: 'day',
              unitDisplay: 'long',
            },
          )}
          subtitle="Holding period"
        />
        <SnapshotBox
          value={numberToPercentageString(
            assetPerformanceAggregations.grossIRR,
          )}
          subtitle="Gross IRR"
        />
      </Grid>
      {assetPerformanceAggregations.wasConverted && (
        <Grid container>
          <Typography variant="italic">
            The valuations above are indicative based on FX rates taken from
            {selectedVehicle.fxDataFeed} on {dateToLabel(new Date())} at
            00:00am.
          </Typography>
        </Grid>
      )}
    </Spacing>
  );
};

export default CompanyPerformanceOverview;
